import classNames from 'classnames';

import { Combobox, Transition } from '@headlessui/react';
import { useQuery } from '@tanstack/react-query';
import { FC, Fragment, useEffect, useState } from 'react';
import { FieldError } from 'react-hook-form';

import { PeopleApi } from '../../api';
import { AddressResponse } from '../../model/Address';

type Props = {
  className?: string;
  error?: FieldError;
  inputClassName?: string;
  placeholder?: string;
  value: AddressResponse | null;
  onChange: (address: AddressResponse) => void;
};

export const AddressAutocomplete: FC<Props> = ({
  className,
  error,
  inputClassName,
  onChange,
  placeholder = 'Введите адрес',
  value,
}) => {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedQuery(query);
    }, 300);
    return () => clearTimeout(handler);
  }, [query]);

  useEffect(() => {
    if (value?.address_text && value.address_text !== query) {
      setQuery(value.address_text);
    }
    if (!value?.address_text) {
      setQuery('');
    }
  }, [value?.address_text]);

  const {
    data: addressesData = { total: 0, items: [] },
    isLoading: isLoading,
    isSuccess,
  } = useQuery({
    queryKey: ['normalizeAddress', debouncedQuery],
    queryFn: () => PeopleApi.normalizeAddress(debouncedQuery),
    enabled: !!debouncedQuery,
  });

  const addresses = addressesData.items;

  const handleSelect = (address: AddressResponse) => {
    onChange(address);
  };

  return (
    <Combobox value={value} onChange={handleSelect}>
      <div className={classNames('relative', className)}>
        <Combobox.Input
          className={classNames(
            'input input--block bg-white text-left',
            inputClassName,
            {
              'input--invalid': Boolean(error),
              '!outline !outline-1 !outline-[red]': Boolean(error),
            },
          )}
          displayValue={(address: AddressResponse | null) =>
            address?.address_text || ''
          }
          onChange={(event) => setQuery(event.target.value)}
          placeholder={placeholder}
        />
        {error?.message ? (
          <p className="mt-1 text-left text-sm text-red">{error.message}</p>
        ) : null}
        <Transition
          as={Fragment}
          enter="transition duration-100 ease-out"
          enterFrom="transform scale-95 opacity-0"
          enterTo="transform scale-100 opacity-100"
          leave="transition duration-75 ease-out"
          leaveFrom="transform scale-100 opacity-100"
          leaveTo="transform scale-95 opacity-0"
        >
          <Combobox.Options className="absolute z-10 mt-[10px] max-h-[300px] w-full overflow-auto rounded-[12px] border border-brand-2 bg-white shadow-lg">
            {isLoading && (
              <Combobox.Option
                disabled
                className="relative cursor-pointer select-none p-[20px] hover:bg-brand-6"
                value={null}
              >
                Загрузка...
              </Combobox.Option>
            )}
            {isSuccess && addresses.length === 0 && (
              <Combobox.Option
                disabled
                className="relative cursor-pointer select-none p-[20px] hover:bg-brand-6"
                value={null}
              >
                Ничего не найдено
              </Combobox.Option>
            )}
            {addresses.map((address) => (
              <Combobox.Option
                key={address.id}
                className="relative cursor-pointer select-none p-[20px] hover:bg-brand-6"
                value={address}
              >
                {({ selected }) => (
                  <span
                    className={classNames('block truncate', {
                      'font-bold': selected,
                    })}
                  >
                    {address.address_text}
                  </span>
                )}
              </Combobox.Option>
            ))}
          </Combobox.Options>
        </Transition>
      </div>
    </Combobox>
  );
};
